import { Subject, SurveyData, SurveyMeta } from '@surveycake/logic-engine';
import { isDevelopment } from 'app/shared/utils/development';
import Cookies from 'js-cookie';
import { http } from './http';
import axios from 'axios';

interface GetDataResponse {
  subjects: Subject[];
  data: SurveyData;
}

/**
 * To get the meta of the survey.
 */
export async function getMeta(svid: string): Promise<SurveyMeta> {
  try {
    const url = http.url.api(`/s/survey/init-meta?${http.query({ svid })}`, 1);
    const res = await http.get(url);
    const { status, data, errmsg = '' } = await http.handleResponse(res);

    if (!status) {
      throw new Error(errmsg);
    }

    if (isDevelopment) {
      Cookies.set('csrftoken', data.csrftoken, {
        expires: 7,
        secure: false,
        path: '/'
      });
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * To get the data of the survey.
 * If `private`, only provide some indeed needed data like language.
 * If `isChildSurvey`, the data won't include the subjects.
 */
export async function getData(
  svid: string,
  previw: boolean
): Promise<GetDataResponse> {
  try {
    const res = await http.get(
      http.url.file(`/json${previw ? '_p' : ''}/${svid}.json`),
      { cache: 'no-cache' }
    );
    const { subjects = [], ...data } = await http.handleResponse(res);
    return { subjects, data: data as SurveyData };
  } catch (error) {
    throw error;
  }
}

/**
 * To get the data of the survey which is private.
 */
export async function getPrivateData(
  svid: string,
  password: string
): Promise<GetDataResponse> {
  try {
    const url = http.url.api(
      `/s/survey/private?${http.query({ svid, password })}`,
      1
    );
    const res = await http.get(url);
    const {
      status,
      data: { subjects = [], ...data } = {},
      errmsg = ''
    } = await http.handleResponse(res);

    if (!status) {
      throw new Error(errmsg);
    }

    return { subjects, data: data as SurveyData };
  } catch (error) {
    throw error;
  }
}

/**
 * To get the data of the survey which is child.
 */
export async function getMasterSubjects(
  svid: string,
  masterid: string,
  password?: string
): Promise<Subject[]> {
  try {
    const url = http.url.api(
      `/s/survey/subjects?${http.query({ svid, masterid, password })}`,
      1
    );
    const res = await http.get(url);
    const { status, data: subjects, errmsg = '' } = await http.handleResponse(
      res
    );

    if (!status) {
      throw new Error(errmsg);
    }

    return subjects;
  } catch (error) {
    throw error;
  }
}

export function getSubjectGroup() {
  return http
    .get(http.url.assets('/subject-group.json'), { cache: 'no-cache' })
    .then(response => http.handleResponse(response));
}

type GetUploadUrlsType = {
  urls: string[];
};

/**
 * Get fileupload urls, and then upload files to that urls
 */
export async function getUploadUrls(
  files: string[]
): Promise<GetUploadUrlsType> {
  try {
    const url = http.url.api(`/s/file/get-upload-urls`, 2);
    const csrftokenFromCookie = Cookies.get('csrftoken');

    const body = JSON.stringify({
      csrftoken: csrftokenFromCookie,
      files
    });

    const headers = {
      'content-type': 'application/json'
    };

    const res = await http.post(url, {
      body,
      headers
    });
    const data = await http.handleResponse(res);

    return data;
  } catch (error) {
    throw new Error(error.errmsg);
  }
}

export async function uploadFile(
  file: File,
  contentType: string,
  url: string,
  onProgress?: (percentage: number) => void
) {
  try {
    const headers = {
      'content-type': contentType
    };

    const response = await axios({
      method: 'put',
      url,
      data: file,
      headers,
      ...(onProgress && {
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      })
      
    });

    if (response.status >= 400 && response.status < 500) {
      throw new Error();
    }
  } catch (error) {
    throw error;
  }
}
